import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useContractDetail } from '@/api/contracts';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { ContractDetailHeader } from './contract-detail-header';
import { ContractInfoCard } from './contract-info-card';
import { SubcontractsTab } from './subcontracts-tab';
import { ProcessesTab } from './processes-tab';
import { ContractUpdatesLog } from './contract-updates-log';
import { ContractAttachmentsSection } from './contract-attachments-section';
import { ContractSuggestionsSection } from './contract-suggestions-section';

const TABS = [
  { value: 'subcontracts', label: 'Subcontracts' },
  { value: 'processes', label: 'Processes' },
] as const;

const TAB_VALUES = TABS.map((t) => t.value) as readonly string[];

/**
 * Contract detail page — identity header (number + status/role/direction pills +
 * lifecycle actions) over the info card, a non-Vietnamese language warning, a
 * Subcontracts/Processes tab switcher persisted in `?tab=` (lazy — each tab
 * mounts only when active), and the change-history log below. Mirrors the
 * customer detail shell (fullwidth, no read-drawer).
 */
export default function ContractDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam) || 0;
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab && TAB_VALUES.includes(requestedTab) ? requestedTab : 'subcontracts';

  const { data, isLoading, isError, refetch } = useContractDetail(id);
  const detail = data?.result;

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const nonVietnamese = detail?.customer_language && detail.customer_language !== 'vi';

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load contract" onRetry={() => refetch()} />
      ) : isLoading || !detail ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <ContractDetailHeader detail={detail} />

          {nonVietnamese ? (
            <Alert tone="warning" title="Non-Vietnamese customer">
              This customer's language is{' '}
              <strong style={{ textTransform: 'uppercase' }}>{detail.customer_language}</strong>. Prepare
              documents accordingly.
            </Alert>
          ) : null}

          <ContractInfoCard detail={detail} />

          {detail.attachments && detail.attachments.length > 0 ? (
            <ContractAttachmentsSection attachments={detail.attachments} />
          ) : null}

          {detail.contract_status === 'pending' ? (
            <ContractSuggestionsSection id={id} />
          ) : null}

          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList>
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="subcontracts">
              {activeTab === 'subcontracts' ? <SubcontractsTab id={id} /> : null}
            </TabsContent>
            <TabsContent value="processes">
              {activeTab === 'processes' ? <ProcessesTab id={id} /> : null}
            </TabsContent>
          </Tabs>

          <ContractUpdatesLog id={id} />
        </div>
      )}
    </Container>
  );
}
